// src/pages/settings/components/SystemSettings.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Divider,
  Button,
  Grid,
  Paper,
  Card,
  CardActionArea,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  SettingsOutlined as SettingsIcon,
  AlternateEmail as EmailIcon,
  Sms as SmsIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  MeetingRoom as IntegrationIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  AttachMoney as BillingIcon,
  Payments as PaymentsIcon,
  People as HRIcon,
  MonetizationOn as CurrencyIcon,
  Business as CompanyIcon,
  Description as TemplateIcon,
  Receipt as TaxIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';

// Import stores
import useSettingsStore from '../../../stores/settingsStore';
import useNotificationStore from '../../../stores/notificationStore';

// Import configuration components
import HRConfiguration from '../../../components/settings/HRConfiguration';
import CurrencyConfiguration from '../../../components/settings/CurrencyConfiguration';

const SystemSettings = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { systemSettings, fetchSystemSettings, updateSystemSetting, isLoading, error } = useSettingsStore();
  const { showSuccess, showError } = useNotificationStore();

  // Fetch system settings on component mount
  useEffect(() => {
    fetchSystemSettings();
  }, []); // Empty dependency array to run only once on mount

  // Email configuration form state
  const [emailConfig, setEmailConfig] = useState({
    provider: 'smtp',
    host: '',
    port: '',
    username: '',
    password: '',
    encryption: 'tls',
    fromEmail: '',
    fromName: '',
  });

  // SMS configuration form state
  const [smsConfig, setSmsConfig] = useState({
    provider: 'twilio',
    accountSid: '',
    authToken: '',
    fromNumber: '',
  });

  // Password policy form state
  const [passwordPolicy, setPasswordPolicy] = useState({
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
    passwordExpiry: 90,
    preventPasswordReuse: 5,
  });

  // Integration settings
  const [integrations, setIntegrations] = useState({
    googleAnalytics: { enabled: false, trackingId: '' },
    facebook: { enabled: false, appId: '', appSecret: '' },
    instagram: { enabled: false, accessToken: '' },
    linkedin: { enabled: false, apiKey: '', apiSecret: '' },
  });

  // General settings
  const [generalSettings, setGeneralSettings] = useState({
    siteName: 'Aghrba Marketing System',
    supportEmail: 'support@example.com',
    maxFileUploadSize: 10,
    defaultLanguage: 'en',
  });

  // Update email config form
  const handleEmailConfigChange = (e) => {
    const { name, value } = e.target;
    setEmailConfig({
      ...emailConfig,
      [name]: value,
    });
  };

  // Update SMS config form
  const handleSmsConfigChange = (e) => {
    const { name, value } = e.target;
    setSmsConfig({
      ...smsConfig,
      [name]: value,
    });
  };

  // Update password policy form
  const handlePasswordPolicyChange = (e) => {
    const { name, value, checked, type } = e.target;
    setPasswordPolicy({
      ...passwordPolicy,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  // Handle integration toggle
  const handleIntegrationToggle = (integration) => (e) => {
    setIntegrations({
      ...integrations,
      [integration]: {
        ...integrations[integration],
        enabled: e.target.checked,
      },
    });
  };

  // Handle integration field change
  const handleIntegrationChange = (integration, field) => (e) => {
    setIntegrations({
      ...integrations,
      [integration]: {
        ...integrations[integration],
        [field]: e.target.value,
      },
    });
  };

  // Handle general settings change
  const handleGeneralSettingChange = (e) => {
    const { name, value } = e.target;
    setGeneralSettings({
      ...generalSettings,
      [name]: value,
    });
  };

  // Test email configuration
  const handleTestEmail = async () => {
    try {
      // In a real app, this would make an API call to test the email configuration
      // For demo purposes, we're simulating success
      showSuccess('Test email sent successfully');
    } catch (error) {
      showError('Failed to send test email');
    }
  };

  // Test SMS configuration
  const handleTestSms = async () => {
    try {
      // In a real app, this would make an API call to test the SMS configuration
      // For demo purposes, we're simulating success
      showSuccess('Test SMS sent successfully');
    } catch (error) {
      showError('Failed to send test SMS');
    }
  };

  // Save general settings
  const handleSaveGeneral = async () => {
    try {
      // In a real app, this would save all general settings at once
      // For demo purposes, we're simulating it
      showSuccess('General settings saved successfully');
    } catch (error) {
      showError('Failed to save general settings');
    }
  };

  // Save email configuration
  const handleSaveEmailConfig = async () => {
    try {
      // In a real app, this would save the email configuration
      // For demo purposes, we're simulating it
      showSuccess('Email configuration saved successfully');
    } catch (error) {
      showError('Failed to save email configuration');
    }
  };

  // Save SMS configuration
  const handleSaveSmsConfig = async () => {
    try {
      // In a real app, this would save the SMS configuration
      // For demo purposes, we're simulating it
      showSuccess('SMS configuration saved successfully');
    } catch (error) {
      showError('Failed to save SMS configuration');
    }
  };

  // Save password policy
  const handleSavePasswordPolicy = async () => {
    try {
      // In a real app, this would save the password policy
      // For demo purposes, we're simulating it
      showSuccess('Password policy saved successfully');
    } catch (error) {
      showError('Failed to save password policy');
    }
  };

  // Save integration settings
  const handleSaveIntegrations = async () => {
    try {
      // In a real app, this would save the integration settings
      // For demo purposes, we're simulating it
      showSuccess('Integration settings saved successfully');
    } catch (error) {
      showError('Failed to save integration settings');
    }
  };

  // Export settings
  const handleExportSettings = () => {
    try {
      // In a real app, this would trigger a download of the settings
      // For demo purposes, we're just simulating success
      showSuccess('Settings exported successfully');
    } catch (error) {
      showError('Failed to export settings');
    }
  };

  // Import settings
  const handleImportSettings = () => {
    // This would typically open a file picker, but for demo purposes,
    // we'll just simulate success
    showSuccess('Settings imported successfully');
  };

  // Loading state
  if (isLoading && Object.keys(systemSettings).length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <AdminIcon sx={{ mr: 1 }} />
          System Settings
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<BackupIcon />}
            onClick={handleExportSettings}
          >
            Export Settings
          </Button>
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={handleImportSettings}
          >
            Import Settings
          </Button>
        </Box>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* Quick Access Cards */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Configuration Centers
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: '100%',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}
          >
            <CardActionArea 
              onClick={() => {
                console.log('Navigating to company settings...');
                navigate('/settings/company');
              }}
              sx={{ height: '100%' }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CompanyIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">Company Settings</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage company information and branding
                    </Typography>
                  </Box>
                  <ArrowIcon />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Configure logo, address, contact details, and bank information
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: '100%',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}
          >
            <CardActionArea 
              onClick={() => {
                console.log('Navigating to quote templates...');
                navigate('/settings/quote-templates');
              }}
              sx={{ height: '100%' }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TemplateIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">Quote Templates</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Design PDF templates for quotes
                    </Typography>
                  </Box>
                  <ArrowIcon />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Create custom templates with your branding and styling
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.05)
              }
            }}
            onClick={() => navigate('/settings/tax-configuration')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TaxIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">Tax Configuration</Typography>
                <Typography variant="body2" color="text.secondary">
                  Set up tax rules and rates
                </Typography>
              </Box>
              <ArrowIcon />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Configure tax regions, rates, and calculation rules
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.05)
              }
            }}
            onClick={() => navigate('/hr-management/configuration')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <HRIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">HR Configuration</Typography>
                <Typography variant="body2" color="text.secondary">
                  HR policies and settings
                </Typography>
              </Box>
              <ArrowIcon />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Leave policies, work schedules, and employee settings
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.05)
              }
            }}
            onClick={() => navigate('/hr-management/payroll/configuration')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CurrencyIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">Currency & Payroll</Typography>
                <Typography variant="body2" color="text.secondary">
                  Currency and payroll settings
                </Typography>
              </Box>
              <ArrowIcon />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Exchange rates, salary components, and payment cycles
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* General Settings */}
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
        >
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 1 }} />
            General Settings
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="System Name"
                name="siteName"
                value={generalSettings.siteName}
                onChange={handleGeneralSettingChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Support Email"
                name="supportEmail"
                type="email"
                value={generalSettings.supportEmail}
                onChange={handleGeneralSettingChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max File Upload Size (MB)"
                name="maxFileUploadSize"
                type="number"
                value={generalSettings.maxFileUploadSize}
                onChange={handleGeneralSettingChange}
                margin="normal"
                InputProps={{ inputProps: { min: 1, max: 100 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Default Language</InputLabel>
                <Select
                  name="defaultLanguage"
                  value={generalSettings.defaultLanguage}
                  onChange={handleGeneralSettingChange}
                  label="Default Language"
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="ar">Arabic</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                
                startIcon={<SaveIcon />}
                onClick={handleSaveGeneral}
              >
                Save General Settings
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* Email Settings */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
        >
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon sx={{ mr: 1 }} />
            Email Configuration
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Email Provider</InputLabel>
                <Select
                  name="provider"
                  value={emailConfig.provider}
                  onChange={handleEmailConfigChange}
                  label="Email Provider"
                >
                  <MenuItem value="smtp">SMTP</MenuItem>
                  <MenuItem value="sendgrid">SendGrid</MenuItem>
                  <MenuItem value="mailgun">Mailgun</MenuItem>
                  <MenuItem value="ses">Amazon SES</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="From Email"
                name="fromEmail"
                type="email"
                value={emailConfig.fromEmail}
                onChange={handleEmailConfigChange}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="From Name"
                name="fromName"
                value={emailConfig.fromName}
                onChange={handleEmailConfigChange}
                margin="normal"
              />
            </Grid>
            
            {emailConfig.provider === 'smtp' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="SMTP Host"
                    name="host"
                    value={emailConfig.host}
                    onChange={handleEmailConfigChange}
                    margin="normal"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="SMTP Port"
                    name="port"
                    type="number"
                    value={emailConfig.port}
                    onChange={handleEmailConfigChange}
                    margin="normal"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Encryption</InputLabel>
                    <Select
                      name="encryption"
                      value={emailConfig.encryption}
                      onChange={handleEmailConfigChange}
                      label="Encryption"
                    >
                      <MenuItem value="tls">TLS</MenuItem>
                      <MenuItem value="ssl">SSL</MenuItem>
                      <MenuItem value="none">None</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="SMTP Username"
                    name="username"
                    value={emailConfig.username}
                    onChange={handleEmailConfigChange}
                    margin="normal"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="SMTP Password"
                    name="password"
                    type="password"
                    value={emailConfig.password}
                    onChange={handleEmailConfigChange}
                    margin="normal"
                  />
                </Grid>
              </>
            )}
            
            {emailConfig.provider !== 'smtp' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="API Key"
                  name="apiKey"
                  type="password"
                  value={emailConfig.apiKey || ''}
                  onChange={handleEmailConfigChange}
                  margin="normal"
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  
                  startIcon={<SaveIcon />}
                  onClick={handleSaveEmailConfig}
                >
                  Save Email Settings
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleTestEmail}
                >
                  Test Email
                </Button>
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* SMS Settings */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
        >
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
            <SmsIcon sx={{ mr: 1 }} />
            SMS Configuration
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>SMS Provider</InputLabel>
                <Select
                  name="provider"
                  value={smsConfig.provider}
                  onChange={handleSmsConfigChange}
                  label="SMS Provider"
                >
                  <MenuItem value="twilio">Twilio</MenuItem>
                  <MenuItem value="nexmo">Nexmo/Vonage</MenuItem>
                  <MenuItem value="aws">AWS SNS</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="From Number"
                name="fromNumber"
                value={smsConfig.fromNumber}
                onChange={handleSmsConfigChange}
                margin="normal"
                placeholder="+1234567890"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account SID/ID"
                name="accountSid"
                value={smsConfig.accountSid}
                onChange={handleSmsConfigChange}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Auth Token/API Key"
                name="authToken"
                type="password"
                value={smsConfig.authToken}
                onChange={handleSmsConfigChange}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  
                  startIcon={<SaveIcon />}
                  onClick={handleSaveSmsConfig}
                >
                  Save SMS Settings
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleTestSms}
                >
                  Test SMS
                </Button>
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* Password Policy */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
        >
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1 }} />
            Password Policy
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Minimum Password Length"
                name="minLength"
                type="number"
                value={passwordPolicy.minLength}
                onChange={handlePasswordPolicyChange}
                margin="normal"
                InputProps={{ inputProps: { min: 6, max: 24 } }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Password Expiry (days)"
                name="passwordExpiry"
                type="number"
                value={passwordPolicy.passwordExpiry}
                onChange={handlePasswordPolicyChange}
                margin="normal"
                InputProps={{ inputProps: { min: 0, max: 365 } }}
                helperText="0 means never expires"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prevent Password Reuse (last N passwords)"
                name="preventPasswordReuse"
                type="number"
                value={passwordPolicy.preventPasswordReuse}
                onChange={handlePasswordPolicyChange}
                margin="normal"
                InputProps={{ inputProps: { min: 0, max: 24 } }}
                helperText="0 means no restriction"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Password Requirements
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      name="requireUppercase"
                      checked={passwordPolicy.requireUppercase}
                      onChange={handlePasswordPolicyChange}
                    />
                  }
                  label="Require uppercase letters"
                />
                <FormControlLabel
                  control={
                    <Switch
                      name="requireLowercase"
                      checked={passwordPolicy.requireLowercase}
                      onChange={handlePasswordPolicyChange}
                    />
                  }
                  label="Require lowercase letters"
                />
                <FormControlLabel
                  control={
                    <Switch
                      name="requireNumbers"
                      checked={passwordPolicy.requireNumbers}
                      onChange={handlePasswordPolicyChange}
                    />
                  }
                  label="Require numbers"
                />
                <FormControlLabel
                  control={
                    <Switch
                      name="requireSpecialChars"
                      checked={passwordPolicy.requireSpecialChars}
                      onChange={handlePasswordPolicyChange}
                    />
                  }
                  label="Require special characters"
                />
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="contained"
                
                startIcon={<SaveIcon />}
                onClick={handleSavePasswordPolicy}
              >
                Save Password Policy
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* HR Configuration */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
        >
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
            <HRIcon sx={{ mr: 1 }} />
            HR Configuration
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <HRConfiguration />
        </AccordionDetails>
      </Accordion>
      
      {/* Currency Configuration */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
        >
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
            <CurrencyIcon sx={{ mr: 1 }} />
            Currency Configuration
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CurrencyConfiguration />
        </AccordionDetails>
      </Accordion>
      
      {/* Tax Configuration */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
        >
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
            <PaymentsIcon sx={{ mr: 1 }} />
            Tax Configuration
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Manage tax configurations for all regions and tax types.
            </Typography>
            <Button
              variant="contained"
              startIcon={<SettingsIcon />}
              onClick={() => navigate('/settings/tax-configuration')}
              sx={{ mt: 2 }}
            >
              Open Tax Configuration
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* External Integrations */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
        >
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
            <IntegrationIcon sx={{ mr: 1 }} />
            External Integrations
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Google Analytics */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">Google Analytics</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={integrations.googleAnalytics.enabled}
                    onChange={handleIntegrationToggle('googleAnalytics')}
                  />
                }
                label={integrations.googleAnalytics.enabled ? 'Enabled' : 'Disabled'}
              />
            </Box>
            
            {integrations.googleAnalytics.enabled && (
              <TextField
                fullWidth
                label="Tracking ID"
                value={integrations.googleAnalytics.trackingId}
                onChange={handleIntegrationChange('googleAnalytics', 'trackingId')}
                margin="normal"
                placeholder="UA-XXXXXXXXX-X or G-XXXXXXXXXX"
              />
            )}
          </Paper>
          
          {/* Facebook Integration */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">Facebook Integration</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={integrations.facebook.enabled}
                    onChange={handleIntegrationToggle('facebook')}
                  />
                }
                label={integrations.facebook.enabled ? 'Enabled' : 'Disabled'}
              />
            </Box>
            
            {integrations.facebook.enabled && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="App ID"
                    value={integrations.facebook.appId}
                    onChange={handleIntegrationChange('facebook', 'appId')}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="App Secret"
                    type="password"
                    value={integrations.facebook.appSecret}
                    onChange={handleIntegrationChange('facebook', 'appSecret')}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            )}
          </Paper>
          
          {/* Instagram Integration */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">Instagram Integration</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={integrations.instagram.enabled}
                    onChange={handleIntegrationToggle('instagram')}
                  />
                }
                label={integrations.instagram.enabled ? 'Enabled' : 'Disabled'}
              />
            </Box>
            
            {integrations.instagram.enabled && (
              <TextField
                fullWidth
                label="Access Token"
                type="password"
                value={integrations.instagram.accessToken}
                onChange={handleIntegrationChange('instagram', 'accessToken')}
                margin="normal"
              />
            )}
          </Paper>
          
          {/* LinkedIn Integration */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">LinkedIn Integration</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={integrations.linkedin.enabled}
                    onChange={handleIntegrationToggle('linkedin')}
                  />
                }
                label={integrations.linkedin.enabled ? 'Enabled' : 'Disabled'}
              />
            </Box>
            
            {integrations.linkedin.enabled && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="API Key"
                    value={integrations.linkedin.apiKey}
                    onChange={handleIntegrationChange('linkedin', 'apiKey')}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="API Secret"
                    type="password"
                    value={integrations.linkedin.apiSecret}
                    onChange={handleIntegrationChange('linkedin', 'apiSecret')}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            )}
          </Paper>
          
          <Button
            variant="contained"
            
            startIcon={<SaveIcon />}
            onClick={handleSaveIntegrations}
          >
            Save Integration Settings
          </Button>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default React.memo(SystemSettings);